import clsx from 'clsx'
import React from 'react'
import { Button } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { AnnouncementMarquee } from '#components/AnnouncementMarquee'
import { AppHeader } from '#components/AppHeader'
import { CompanySideNav } from '#components/CompanySideNav'
import { GlobalControlBar } from '#components/GlobalControlBar'
import { Icon } from '#components/Icon'
import { ImpersonationBanner } from '#components/ImpersonationBanner'
import { RoleGuard } from '#components/RoleGuard'
import { ExpandableNavItem, NavigationItem, NavigationSection, SideNav } from '#components/SideNav'
import { UserSideNav } from '#components/UserSideNav'
import { useAuth } from '#hooks/useAuth'
import { useFetchActiveAnnouncements } from '#hooks/useFetchActiveAnnouncements'
import { useFetchPendingActionsStats } from '#hooks/useFetchPendingActionsStats'
import { useFetchPendingApprovals } from '#hooks/useFetchPendingApprovals'
import { useSidebar } from '#hooks/useSidebar'
import { useUserNav } from '#hooks/useUserNav'
import { getHighestRole, ROLE_ADMIN, ROLE_MANAGER, ROLE_SUPER_ADMIN, ROLE_USER } from '#interfaces/auth'
import { useFetchCompanyName } from '#pages/CompanyDetail/hooks/useFetchCompanyName'
import { useFetchCrashReportStats } from '#pages/ErrorReports/hooks/useFetchCrashReportStats'
import { CompanyContractProvider } from '#providers/CompanyContractProvider'
import { ShellTitleProvider } from '#providers/ShellTitleProvider'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

/** Standalone company sidebar for company admin users — no dual-panel, no super admin nav */
const CompanyAdminSidebar = ({
  companyId,
  companyName,
}: {
  companyId: string
  companyName: string
}): React.JSX.Element => {
  const { t } = useTranslation()
  const { isCollapsed, isMobile, toggleSidebar } = useSidebar()

  return (
    <div className={clsx(styles.standaloneNav, isCollapsed && styles.standaloneNavCollapsed)} data-testid="side-nav">
      <nav className={styles.standalonePanel} aria-label={t('Company navigation')}>
        <CompanyContractProvider companyId={companyId}>
          <CompanySideNav companyId={companyId} companyName={companyName} showBackLink={false} />
        </CompanyContractProvider>
      </nav>

      {!isMobile && (
        <div className={styles.standaloneToggle}>
          <Button
            className={styles.standaloneToggleButton}
            onPress={toggleSidebar}
            aria-label={isCollapsed ? t('Expand sidebar') : t('Collapse sidebar')}
            data-testid="sidebar-toggle"
          >
            <Icon
              name="chevron-left"
              width="16"
              height="16"
              className={clsx(styles.standaloneToggleIcon, isCollapsed && styles.standaloneToggleIconRotated)}
            />
          </Button>
        </div>
      )}
    </div>
  )
}

/** Standalone user sidebar for ROLE_USER users — self-view with root-level paths */
const UserRoleSidebar = (): React.JSX.Element => {
  const { t } = useTranslation()
  const { isCollapsed, isMobile, toggleSidebar } = useSidebar()
  const userNav = useUserNav()

  return (
    <div className={clsx(styles.standaloneNav, isCollapsed && styles.standaloneNavCollapsed)} data-testid="side-nav">
      <nav className={styles.standalonePanel} aria-label={t('Main navigation')}>
        <UserSideNav
          userId={userNav?.userId ?? ''}
          userName={userNav?.userName ?? ''}
          companyId={userNav?.companyId ?? ''}
          companyName={userNav?.companyName ?? ''}
          isSelfView
        />
      </nav>

      {!isMobile && (
        <div className={styles.standaloneToggle}>
          <Button
            className={styles.standaloneToggleButton}
            onPress={toggleSidebar}
            aria-label={isCollapsed ? t('Expand sidebar') : t('Collapse sidebar')}
            data-testid="sidebar-toggle"
          >
            <Icon
              name="chevron-left"
              width="16"
              height="16"
              className={clsx(styles.standaloneToggleIcon, isCollapsed && styles.standaloneToggleIconRotated)}
            />
          </Button>
        </div>
      )}
    </div>
  )
}

/** Super admin sidebar content with all global navigation sections */
const SuperAdminSidebar = (): React.JSX.Element => {
  const { t } = useTranslation()
  const { totalPendingPropositions } = useFetchPendingApprovals()
  const { stats: crashReportStats } = useFetchCrashReportStats()
  const { stats: pendingActionsStats } = useFetchPendingActionsStats()

  return (
    <SideNav>
      <NavigationSection title={t('Overview')}>
        <NavigationItem label={t('Dashboard')} location="/" icon="squares" exact />
        <NavigationItem label={t('Companies')} location="/companies" icon="building" />
        <ExpandableNavItem label={t('Card information')} icon="credit-card" storageKey="products">
          <NavigationItem label={t('Personal cards')} location="/ov-cards" isChild />
          <NavigationItem label={t('Shared cards')} location="/shared-cards" isChild />
          <NavigationItem label={t('Day tickets')} location="/day-tickets" isChild />
        </ExpandableNavItem>
        <ExpandableNavItem label={t('Subscriptions')} icon="clock" storageKey="subscriptions">
          <NavigationItem label={t('Active management')} location="/subscriptions" isChild />
          <NavigationItem label={t('Export')} location="/subscriptions/export" isChild />
          <RoleGuard requiredRole={ROLE_SUPER_ADMIN}>
            <NavigationItem label={t('Subscription types')} location="/subscription-types" isChild />
          </RoleGuard>
        </ExpandableNavItem>
      </NavigationSection>

      <RoleGuard requiredRole={ROLE_SUPER_ADMIN}>
        <NavigationSection title={t('Orders')}>
          <NavigationItem
            label={t('Card orders')}
            location="/approval/pending"
            icon="credit-card"
            badge={totalPendingPropositions > 0 ? String(totalPendingPropositions) : undefined}
          />
        </NavigationSection>

        <NavigationSection title={t('Financial')}>
          <NavigationItem label={t('Transactions')} location="/transactions" icon="receipt" />
          <NavigationItem label={t('Exports')} location="/exports" icon="download" />
        </NavigationSection>

        <NavigationSection title={t('System')}>
          <NavigationItem label={t('Customer management')} location="/customer-management" icon="users" />
          <NavigationItem label={t('Dashboard templates')} location="/dashboard-templates" icon="squares" />
          <NavigationItem label={t('Email templates')} location="/email-templates" icon="mail" />
          <NavigationItem label={t('Audit log')} location="/audit-log" icon="shield-check" />
          <NavigationItem
            label={t('Error reports')}
            location="/error-reports"
            icon="alert-triangle"
            badge={
              crashReportStats && crashReportStats.unresolved > 0 ? String(crashReportStats.unresolved) : undefined
            }
          />
        </NavigationSection>

        <NavigationSection title={t('Misc')}>
          <NavigationItem label={t('All products')} location="/all-products" icon="credit-card" />
          <NavigationItem label={t('Brand products')} location="/brand-products" icon="ticket" />
          <NavigationItem label={t('Travel costs')} location="/travel-costs" icon="receipt" />
          <NavigationItem
            label={t('Imports')}
            location="/imports"
            icon="upload"
            badge={
              pendingActionsStats && pendingActionsStats.pendingImports > 0
                ? String(pendingActionsStats.pendingImports)
                : undefined
            }
          />
          <NavigationItem label={t('Email log')} location="/email-log" icon="mail" />
          <NavigationItem label={t('Settings')} location="/settings" icon="cog" />
          <NavigationItem label={t('Login as')} location="/login-as" icon="users" />
        </NavigationSection>
      </RoleGuard>
    </SideNav>
  )
}

export const FullLayout = ({ children }: IProps): React.JSX.Element => {
  const { user } = useAuth()
  const { isCollapsed, isMobile, setCollapsed } = useSidebar()
  const { announcements } = useFetchActiveAnnouncements()
  const hasAnnouncements = announcements.length > 0

  const highestRole = getHighestRole(user?.roles)
  const isUser = highestRole === ROLE_USER
  const isCompanyLevel = highestRole === ROLE_ADMIN || highestRole === ROLE_MANAGER
  const isCompanyReady = isCompanyLevel && !!user?.companyId
  const isCompanyWaiting = isCompanyLevel && !user?.companyId
  const { companyName } = useFetchCompanyName(isCompanyReady ? user!.companyId! : '')

  return (
    <ShellTitleProvider>
      <div className={styles.layout}>
        <div className={styles.header}>
          <GlobalControlBar />
          <AnnouncementMarquee announcements={announcements} />
          <AppHeader />
          <ImpersonationBanner />
        </div>

        <div className={styles.body}>
          <div
            className={clsx(
              styles.sidebar,
              isMobile && styles.sidebarMobile,
              isMobile && isCollapsed && styles.sidebarHidden,
              hasAnnouncements && styles.withAnnouncementBar,
            )}
          >
            {isCompanyWaiting ? (
              <div className={styles.standaloneNav} data-testid="side-nav" />
            ) : isUser ? (
              <UserRoleSidebar />
            ) : isCompanyReady ? (
              <CompanyAdminSidebar companyId={user!.companyId!} companyName={companyName} />
            ) : (
              <SuperAdminSidebar />
            )}
          </div>

          {isMobile && !isCollapsed && (
            <div
              className={clsx(styles.backdrop, hasAnnouncements && styles.withAnnouncementBar)}
              onClick={(): void => setCollapsed(true)}
              aria-hidden="true"
              data-testid="sidebar-backdrop"
            />
          )}

          <div className={styles.container}>
            <main className={styles.main} data-testid="main-content">
              {children}
            </main>
          </div>
        </div>
      </div>
    </ShellTitleProvider>
  )
}
